/**
 * useTopicTypeColorMap · Topic Type Chip 颜色映射 Hook
 *
 * - 通过 cond 接口拉 L1/L2 类目结构（cond key 由调用方指定，支持跨业务复用）
 *   - CreativeTrends:    `creative_trends_config_topic_types`（默认）
 *   - UgcCreativeTrends: `ugc_trends_config_topic_types`
 * - 按 L1 在 cond 数组中的索引从 `AI_TAGS_COLORS` 取配色（参考 Opinion TagList）
 * - 返回 `{ L1.label_en → IAITagColor }` 映射；L2 chip 共享 parent 的颜色
 *
 * ⚠️ 在主页面 `index.tsx` 调用一次，结果通过 props 传给 CardView / ListView 等模块，
 *   避免每个模块重复请求 / 维护副本。
 */
import { useMemo } from 'react';
import { useFilterCondData } from '@opm/components/FilterCommon/hooks/useFilterCondData';
import { AI_TAGS_COLORS, TTopicTypeColorMap } from '../utils';

/** cond 接口 key —— 不同业务使用不同 key，但 shape 一致 */
export type TTopicTypeCondKey =
  | 'creative_trends_config_topic_types'
  | 'ugc_trends_config_topic_types';

interface ICondTopicType {
  value: string;
  label_en: string;
  label_zh: string;
  children?: Array<{ value: string; label_en: string; label_zh: string }>;
}

interface IUseTopicTypeColorMapResult {
  topicTypeColorMap: TTopicTypeColorMap;
  loading: boolean;
}

export function useTopicTypeColorMap(
  condKey: TTopicTypeCondKey = 'creative_trends_config_topic_types',
): IUseTopicTypeColorMapResult {
  const { condData, loading } = useFilterCondData({
    keys: [],
    extrRequestKeys: [condKey],
  });

  const topicTypeColorMap = useMemo<TTopicTypeColorMap>(() => {
    const list: ICondTopicType[] = condData?.[condKey] || [];
    const map: TTopicTypeColorMap = {};
    list.forEach((item, idx) => {
      if (!item?.label_en) return;
      const color = AI_TAGS_COLORS[idx % AI_TAGS_COLORS.length];
      if (!color) return;
      map[item.label_en] = color;
    });
    return map;
  }, [condData, condKey, AI_TAGS_COLORS]);

  return { topicTypeColorMap, loading };
}
